import {useMutation} from '@tanstack/react-query';

import {UserIdentity, UserIdentityBasic} from '../../../../../../typings/user';
import {apiClient} from '../../../../../utils/api-client';

import {QueryPermission, useGetQueryPermissionsCache} from './use-get-query-permitted-users';
import {showErrorNotification} from '../../../../../providers/notifications-center/show-notification';
import {useRecentQueriesCache} from '../../../queries-sidebar/hooks/use-recent-queries';
import {useFavoriteQueriesCache} from '../../../queries-sidebar/hooks/use-favorite-queries';

interface MutationParams {
    queryId: number;
    user: UserIdentityBasic;
    requestMode: 'share' | 'unshare';
}

interface MutationContext {
    previousPermissions: QueryPermission[];
}

function shareQueryRequest({queryId, user, requestMode}: MutationParams) {
    return apiClient.post({
        path: `queries-service/api/queries/${queryId}/${
            requestMode === 'share' ? 'share' : 'unshare'
        }`,
        parameters: {
            userEmail: user.email
        }
    });
}

export function useShareQueryMutation(queryId: number, sharedBy: UserIdentity) {
    const getQueryPermittedUsersCache = useGetQueryPermissionsCache(queryId);

    const recentQueriesCache = useRecentQueriesCache();
    const favoriteQueriesCache = useFavoriteQueriesCache();

    return useMutation<unknown, Error, MutationParams, MutationContext>({
        mutationFn: shareQueryRequest,
        onMutate: async (params: MutationParams) => {
            const previousPermissions = await getQueryPermittedUsersCache.onChangeUserPermission(
                params.user,
                sharedBy,
                params.requestMode
            );

            return {previousPermissions};
        },
        onError: (error, params, context) => {
            showErrorNotification()(error);
            if (context) {
                getQueryPermittedUsersCache.setPermissions(context.previousPermissions);
            }
        },
        onSuccess: (data, mutationParams, context) => {
            if (mutationParams.requestMode === 'share') {
                recentQueriesCache.updateExistingQuery({id: Number(queryId), shared: true});
                favoriteQueriesCache.updateExistingQuery({id: Number(queryId), shared: true});

                return;
            }

            const wasSharedWithOnlyUser = context.previousPermissions.length === 1;
            if (wasSharedWithOnlyUser) {
                recentQueriesCache.updateExistingQuery({id: Number(queryId), shared: false});
                favoriteQueriesCache.updateExistingQuery({id: Number(queryId), shared: false});
            }
        }
    });
}
